import React from "react";
import useUpload from "../hooks/useUpload";
import useUploadDocument from "../../hooks/useUploadDocument";
import TextInput from "../../../components/elements/form/TextInput";
import SelectDate from "../../../components/elements/form/SelectDate";
import UploadInput from "../../../components/elements/form/UploadInput";

import Button from "../../../components/elements/form/Button";
import { get_document, update_document } from "../../../service/api/document";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store";
import { FaFileLines } from "react-icons/fa6";
import { setTitle } from "../../../store/slices/navbar";
import { setDocument } from "../../../store/slices/document";
import { Loader } from "../../../components/animation/Loader";

const EditUploadDocument = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const loading = useAppSelector(({ document }) => document.loading);
  const document = useAppSelector((state) => state.document.document);
  const [loadingDocument, setLoadingDocument] = React.useState(true);

  React.useEffect(() => {
    dispatch(setDocument(null));
    if (!id) {
      setLoadingDocument(false);
      return;
    }

    let active = true;
    setLoadingDocument(true);
    get_document(dispatch, id).finally(() => {
      if (active) setLoadingDocument(false);
    });

    return () => {
      active = false;
    };
  }, [dispatch, id]);

  React.useEffect(() => {
    if (document?.name) {
      dispatch(setTitle(`Edit - ${document.name}`));
    }
  }, [document?.name, dispatch]);

  const { data, dataSet, error, errorSet, onValidate, onChange } = useUpload();

  React.useEffect(() => {
    if (!document || String(document.id) !== id) return;

    const toInputDate = (date: Date | string) =>
      new Date(date).toISOString().slice(0, 10);
    dataSet({
      name: document.name,
      issuing_body: document.issuing_body,
      issuing_date: toInputDate(document.issuing_date),
      expiring_date: toInputDate(document.expiring_date),
      document_file: document.document_file,
    });
  }, [dataSet, document, id]);

  const { progress, uploadDocument } = useUploadDocument();
  const documentLoaded = document && String(document.id) === id;

  const handleSubmit = () => {
    if (id && onValidate()) {
      update_document(dispatch, id, data, navigate);
    }
  };

  if ((loading || loadingDocument) && !documentLoaded) {
    return <Loader />;
  }

  if (!documentLoaded) {
    return (
      <div className="rounded-xl bg-white p-6 text-center text-gray-600">
        Document could not be loaded.
      </div>
    );
  }

  return (
    <div className="">
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 space-y-6">
          <div className="w-full">
            <UploadInput
              title="Upload Document"
              name="document"
              label="PDF Document"
              completed={Boolean(data.document_file)}
              status={error.document_file}
              loading={progress}
              onChange={async (file: File | File[]) => {
                const fileToUpload = Array.isArray(file) ? file[0] : file;
                const path = await uploadDocument(fileToUpload, "Document");
                if (path && path.length > 0) {
                  dataSet((prev) => ({
                    ...prev,
                    document_file: path[0].file_url,
                  }));
                  errorSet((prev) => ({ ...prev, document_file: "" }));
                }
              }}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <TextInput
              label="Document Name"
              name="name"
              value={data.name}
              type="text"
              placeholder="Enter document name"
              required={true}
              status={error.name}
              onChange={onChange}
              icon={<FaFileLines size={20} />}
            />

            <TextInput
              label="Issuing Body"
              name="issuing_body"
              value={data.issuing_body}
              type="text"
              placeholder="Enter issuing body"
              required={true}
              status={error.issuing_body}
              onChange={onChange}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <SelectDate
              label="Issuing Date"
              name="issuing_date"
              value={data.issuing_date}
              placeholder="Select issuing date"
              required={true}
              status={error.issuing_date}
              onChange={onChange}
            />

            <SelectDate
              label="Expiring Date"
              name="expiring_date"
              value={data.expiring_date}
              placeholder="Select expiring date"
              required={true}
              status={error.expiring_date}
              onChange={onChange}
            />
          </div>

          <div className="flex justify-end">
            <Button
              title="Update Document"
              loading={loading}
              onHandler={handleSubmit}
              className="w-full md:w-auto px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUploadDocument;
